import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { storeConfig } from '../../../../store.config'
import { ROUTES } from '../../../constants/routes.constants'
import { Divider, SiteFooter, StorefrontNav, useFadeUp } from '../../components/storefront/chrome'
import { Reveal } from '../../motion'
import { Spinner } from '../../components/atoms/Spinner'
import { useBuildProduct } from '../../../viewmodels/useBuildProduct'
import { BuildMediaLightbox } from './BuildMediaLightbox'
import { isVideo, buildMediaUrl, PlayBadge } from './buildMedia'
import { useCartContext } from '../../../context/CartContext'
import { useProfileContext } from '../../../context/ProfileContext'
import type { BuildMediaDto, BuildVariantDto } from '../../../models/build.model'

/**
 * `/build/:id` — parallel to ShopProductPage. Identical UX; sourced
 * from /build/product/:id and /build/media/:id for the future
 * store-builder flow.
 */
export function BuildProductPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>()
  const vm = useBuildProduct(id ?? null)
  const fade = useFadeUp()
  const spec = storeConfig.products.catalog[0]
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const profile = useProfileContext()
  const [quantity, setQuantity] = useState(1)

  useEffect(() => {
    setQuantity(1)
  }, [vm.selectedVariantId])

  return (
    <>
      <Helmet>
        <title>{`Build · ${storeConfig.brand.name}`}</title>
      </Helmet>
      <StorefrontNav onProfile={() => profile.open()} fullWidth showCart showBuild />

      <main className="relative z-10 mx-auto max-w-5xl px-5 pt-28 pb-20 sm:pt-32">
        {vm.isLoading ? (
          <div className="flex items-center justify-center py-32">
            <Spinner />
          </div>
        ) : vm.error !== null ? (
          <Panel>
            <p className="font-body text-body text-charcoal">{vm.error}</p>
            <button
              type="button"
              onClick={vm.reload}
              className="mt-4 rounded-full border border-border px-5 py-2 font-body text-body-sm text-charcoal transition hover:border-forest hover:text-forest"
            >
              Try again
            </button>
          </Panel>
        ) : vm.product === null ? (
          <Panel>
            <h1 className="font-heading text-h2 text-charcoal">Coming soon</h1>
            <p className="mt-3 font-body text-body text-text-secondary">
              Our first product is almost ready. Check back shortly.
            </p>
            <Link
              to={ROUTES.HOME}
              className="mt-6 inline-block rounded-full border border-border px-5 py-2 font-body text-body-sm text-charcoal transition hover:border-forest hover:text-forest"
            >
              Back home
            </Link>
          </Panel>
        ) : (
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.12 } } }}
            className="grid gap-10 md:grid-cols-2 md:gap-12"
          >
            <motion.div variants={fade}>
              <Gallery
                media={vm.product.media}
                productName={vm.product.name}
                onOpenLightbox={(i) => setLightboxIndex(i)}
              />
            </motion.div>

            <motion.div variants={fade} className="flex flex-col">
              <h1 className="font-heading text-h1 text-charcoal leading-tight">{vm.product.name}</h1>

              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-body text-body-sm text-text-secondary">
                {spec !== undefined && spec.minAgeMonths !== null && spec.maxAgeMonths !== null ? (
                  <span>{`${spec.minAgeMonths}–${spec.maxAgeMonths} months`}</span>
                ) : null}
                {spec !== undefined && spec.maxWeightKg !== null ? (
                  <>
                    <Dot />
                    <span>{`up to ${spec.maxWeightKg} kg`}</span>
                  </>
                ) : null}
              </div>

              <div className="my-5">
                <Divider />
              </div>

              <PriceBlock variant={vm.selectedVariant} />
              {/* /build intentionally omits stock badges + variant
                  greying — selection is unconstrained by warehouse
                  on-hand. Kiran 2026-06-06. */}

              {vm.product.variants.length > 1 ? (
                <VariantSelector
                  variants={vm.product.variants}
                  selectedId={vm.selectedVariantId}
                  onSelect={vm.selectVariant}
                />
              ) : null}

              <QuantityStepper
                variant={vm.selectedVariant}
                quantity={quantity}
                onChange={setQuantity}
              />

              <AddToCartButton variant={vm.selectedVariant} quantity={quantity} />

              {vm.product.description.trim() !== '' ? (
                <Reveal travel={32} duration={0.9} className="mt-8">
                  <p className="font-body text-body text-charcoal/90 leading-relaxed whitespace-pre-line">
                    {vm.product.description}
                  </p>
                </Reveal>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </main>

      <SiteFooter />

      {vm.product !== null && lightboxIndex !== null ? (
        <BuildMediaLightbox
          media={vm.product.media}
          productName={vm.product.name}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      ) : null}

    </>
  )
}

function Panel({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="mx-auto max-w-xl rounded-[2rem] border border-border/40 bg-bg-primary/70 px-8 py-12 text-center shadow-lg backdrop-blur-md">
      {children}
    </div>
  )
}

function Dot(): JSX.Element {
  return <span aria-hidden className="h-1 w-1 rounded-full bg-text-secondary/60" />
}


function Gallery({
  media,
  productName,
  onOpenLightbox
}: {
  readonly media: ReadonlyArray<BuildMediaDto>
  readonly productName: string
  readonly onOpenLightbox: (index: number) => void
}): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    setActiveIndex(0)
    scrollerRef.current?.scrollTo({ left: 0 })
  }, [media])

  if (media.length === 0) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded-[1.75rem] border border-border/40 bg-parchment/50 backdrop-blur-sm">
        <span className="font-body text-body-sm text-text-secondary">Photos coming soon</span>
      </div>
    )
  }

  const multiple = media.length > 1

  const scrollToIndex = (i: number): void => {
    const el = scrollerRef.current
    if (el === null) return
    const clamped = Math.max(0, Math.min(i, media.length - 1))
    el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' })
  }

  const handleScroll = (): void => {
    const el = scrollerRef.current
    if (el === null) return
    const i = Math.round(el.scrollLeft / el.clientWidth)
    if (i !== activeIndex) setActiveIndex(i)
  }

  return (
    <div>
      <div className="relative">
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain rounded-[1.75rem] border border-border/40 bg-bg-primary/40 shadow-md backdrop-blur-sm [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {media.map((m, i) =>
            isVideo(m.content_type) ? (
              <GalleryVideo
                key={m.media_id}
                src={buildMediaUrl(m.media_id)}
                label={m.alt_text ?? `${productName} video ${i + 1}`}
              />
            ) : (
              <img
                key={m.media_id}
                src={buildMediaUrl(m.media_id)}
                alt={m.alt_text ?? `${productName} image ${i + 1}`}
                draggable={false}
                onClick={() => onOpenLightbox(i)}
                className="aspect-square w-full shrink-0 cursor-zoom-in snap-center object-cover"
              />
            )
          )}
        </div>

        <button
          type="button"
          onClick={() => onOpenLightbox(activeIndex)}
          aria-label="Open full screen"
          title="Open full screen"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-border/50 bg-bg-primary/80 text-charcoal shadow-sm backdrop-blur-sm transition hover:bg-bg-primary"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </button>

        {multiple ? (
          <>
            <GalleryArrow
              dir="prev"
              disabled={activeIndex === 0}
              onClick={() => scrollToIndex(activeIndex - 1)}
            />
            <GalleryArrow
              dir="next"
              disabled={activeIndex === media.length - 1}
              onClick={() => scrollToIndex(activeIndex + 1)}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center gap-1.5">
              {media.map((m, i) => (
                <span
                  key={m.media_id}
                  className={`h-1.5 rounded-full transition-all ${
                    i === activeIndex ? 'w-4 bg-forest' : 'w-1.5 bg-bg-primary/80'
                  }`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      {multiple ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {media.map((m, i) => (
            <button
              key={m.media_id}
              type="button"
              onClick={() => scrollToIndex(i)}
              aria-label={`View item ${i + 1}`}
              className={`relative h-16 w-16 overflow-hidden rounded-xl border transition ${
                i === activeIndex ? 'border-forest ring-1 ring-forest' : 'border-border/50 hover:border-forest/60'
              }`}
            >
              {isVideo(m.content_type) ? (
                <>
                  <video
                    src={`${buildMediaUrl(m.media_id)}#t=0.1`}
                    muted
                    preload="metadata"
                    className="h-full w-full bg-black object-cover"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                    <PlayBadge />
                  </span>
                </>
              ) : (
                <img
                  src={buildMediaUrl(m.media_id)}
                  alt={m.alt_text ?? `${productName} thumbnail ${i + 1}`}
                  className="h-full w-full object-cover"
                />
              )}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function GalleryArrow({
  dir,
  disabled,
  onClick
}: {
  readonly dir: 'prev' | 'next'
  readonly disabled: boolean
  readonly onClick: () => void
}): JSX.Element {
  const isPrev = dir === 'prev'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={isPrev ? 'Previous image' : 'Next image'}
      className={`absolute top-1/2 ${isPrev ? 'left-3' : 'right-3'} flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border/50 bg-bg-primary/80 text-charcoal shadow-sm backdrop-blur-sm transition hover:bg-bg-primary disabled:pointer-events-none disabled:opacity-0`}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {isPrev ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
      </svg>
    </button>
  )
}

function GalleryVideo({ src, label }: { readonly src: string; readonly label: string }): JSX.Element {
  const ref = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const play = (): void => {
    const v = ref.current
    if (v === null) return
    void v.play().catch(() => {})
  }
  return (
    <div className="relative aspect-square w-full shrink-0 snap-center bg-black">
      <video
        ref={ref}
        src={src}
        controls={playing}
        playsInline
        preload="metadata"
        aria-label={label}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        className="h-full w-full object-contain"
      />
      {!playing ? (
        <button
          type="button"
          onClick={play}
          aria-label="Play video"
          className="absolute inset-0 z-20 flex items-center justify-center"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-bg-primary/85 shadow-lg transition hover:scale-105">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" className="ml-1 text-charcoal" aria-hidden>
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </button>
      ) : null}
    </div>
  )
}

function formatRupees(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function PriceBlock({ variant }: { readonly variant: BuildVariantDto | null }): JSX.Element {
  const price = variant?.selling_price_paisa ?? null
  if (price === null) {
    return <p className="font-heading text-h3 text-text-secondary">Price coming soon</p>
  }
  return <p className="font-heading text-h2 text-charcoal">{formatRupees(price)}</p>
}

function VariantSelector({
  variants,
  selectedId,
  onSelect
}: {
  readonly variants: ReadonlyArray<BuildVariantDto>
  readonly selectedId: string | null
  readonly onSelect: (variantId: string) => void
}): JSX.Element {
  // /build doesn't gate variants by stock — every option is selectable.
  return (
    <div className="mt-6">
      <p className="mb-2 font-body text-body-xs uppercase tracking-wider text-text-secondary">Options</p>
      <div className="flex flex-wrap gap-2">
        {variants.map((v) => {
          const selected = v.variant_id === selectedId
          return (
            <button
              key={v.variant_id}
              type="button"
              onClick={() => onSelect(v.variant_id)}
              aria-pressed={selected}
              className={`rounded-full border px-4 py-1.5 font-body text-body-sm transition ${
                selected
                  ? 'border-forest bg-forest/10 text-charcoal'
                  : 'border-border text-charcoal hover:border-forest/60'
              }`}
            >
              {v.axes_label ?? 'Standard'}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * /build's QuantityStepper is unbounded — no warehouse-on-hand cap, no
 * per-product max. Customer can type any positive integer (Kiran
 * 2026-06-06). Still hides when there's no variant or no price set.
 */
function QuantityStepper({
  variant,
  quantity,
  onChange
}: {
  readonly variant: BuildVariantDto | null
  readonly quantity: number
  readonly onChange: (q: number) => void
}): JSX.Element | null {
  const [draft, setDraft] = useState<string>(String(quantity))
  useEffect(() => { setDraft(String(quantity)) }, [quantity])

  if (variant === null || variant.selling_price_paisa === null) {
    return null
  }
  const canDecrement = quantity > 1

  const commit = (raw: string): void => {
    const n = Number(raw)
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
      setDraft(String(quantity))
      return
    }
    onChange(n)
    setDraft(String(n))
  }

  return (
    <div className="mt-6 flex items-center gap-3">
      <p className="font-body text-body-xs uppercase tracking-wider text-text-secondary">Quantity</p>
      <div className="inline-flex items-center rounded-full border border-border bg-bg-primary/60">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, quantity - 1))}
          disabled={!canDecrement}
          aria-label="Decrease quantity"
          className="flex h-9 w-9 items-center justify-center rounded-full text-h3 text-charcoal transition hover:bg-forest/10 disabled:cursor-not-allowed disabled:opacity-30"
        >
          −
        </button>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
          aria-label="Quantity"
          className="h-9 w-16 bg-transparent text-center font-mono text-body tabular-nums focus:outline-none focus:ring-2 focus:ring-forest/40 rounded"
        />
        <button
          type="button"
          onClick={() => onChange(quantity + 1)}
          aria-label="Increase quantity"
          className="flex h-9 w-9 items-center justify-center rounded-full text-h3 text-charcoal transition hover:bg-forest/10"
        >
          +
        </button>
      </div>
    </div>
  )
}

function AddToCartButton({
  variant,
  quantity
}: {
  readonly variant: BuildVariantDto | null
  readonly quantity: number
}): JSX.Element {
  const { addItem, isLoading: cartBusy } = useCartContext()
  const [notice, setNotice] = useState<'added' | 'error' | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (notice === null) return
    const t = window.setTimeout(() => { setNotice(null); setErrorMsg(null) }, 2500)
    return () => window.clearTimeout(t)
  }, [notice])

  // No stock gate on /build — only block when there's no variant, no
  // price set, or the cart request is mid-flight.
  const noPrice = variant !== null && variant.selling_price_paisa === null
  const disabled = variant === null || noPrice || cartBusy
  const label = quantity > 1 ? `Add ${quantity} to cart` : 'Add to cart'

  const onClick = async (): Promise<void> => {
    if (variant === null || noPrice) return
    // CartContext derives kind='build' from the current /build/* path
    // and useCart sends it on the wire; per-product cap + tenant
    // cart-value cap are skipped server-side for /build adds. Kiran
    // 2026-06-06.
    const result = await addItem(variant.variant_id, quantity)
    if (result.ok) {
      setNotice('added')
    } else {
      setNotice('error')
      setErrorMsg(result.message)
    }
  }

  return (
    <div className="mt-7">
      <button
        type="button"
        disabled={disabled}
        onClick={() => { void onClick() }}
        className={`w-full rounded-full px-6 py-3 font-body text-body shadow-md transition sm:w-auto sm:px-10 ${
          disabled
            ? 'cursor-not-allowed bg-forest/40 text-charcoal'
            : 'bg-forest text-charcoal hover:bg-forest/90 hover:shadow-lg'
        }`}
      >
        {label}
      </button>
      {notice !== null ? (
        <p className="mt-2 font-body text-body-xs text-text-secondary" aria-live="polite">
          {notice === 'added' ? 'Added to cart.' : (errorMsg ?? 'Could not add to cart.')}
        </p>
      ) : null}
    </div>
  )
}
